import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type {
  LeaveBalanceDto,
  OrganisationBalancesParams,
  UserBalancesParams,
} from '@/utils/api/leave-service/models'
import type { UserDetailsResponse } from '@/utils/api/user-service/models'

import { OrganisationSearchInput } from '@/components/common/OrganisationSearchInput'
import { UserSearchInput } from '@/components/common/UserSearchInput'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser } from '@/lib/role-guards'
import {
  useForfeitExpiredCarryover,
  useOrganisationBalances,
  useTriggerYearEndRollover,
  useUserBalances,
} from '@/utils/api/leave-service'
import { getAllUserDetails, useGetAllOrganisations } from '@/utils/api/user-service'
import { formatDate } from '@/utils/DateHelpers'

interface OrganisationBalanceRow extends LeaveBalanceDto {
  userName: string
  userEmail: string
}

interface OrganisationUserSummaryRow {
  userId: number
  userName: string
  userEmail: string
  leaveTypes: number
  daysTakenToDate: number
  carryoverRemaining: number
  borrowedDays: number
  totalRemaining: number
  effectiveBalance: number
}

function formatDays(value?: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—'
  }
  return `${new Intl.NumberFormat().format(value)} days`
}

export function LeaveBalancesPage() {
  const { user: currentUser } = useAuth()
  const canFilterByOrganisation = isAdminUser(currentUser)
  const currentYear = new Date().getFullYear()
  const previousYear = currentYear - 1
  const defaultOrganisationFilter = useMemo(() => {
    if (typeof currentUser?.mainOrgainisationId === 'number') {
      return String(currentUser.mainOrgainisationId)
    }
    return ''
  }, [currentUser?.mainOrgainisationId])

  const [rolloverFromYear, setRolloverFromYear] = useState(() => previousYear.toString())
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false)
  const [organisationDetailsDialogOpen, setOrganisationDetailsDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'organisation' | 'user'>('organisation')
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined)
  const [selectedOrganisationDetailUserId, setSelectedOrganisationDetailUserId] = useState<number | undefined>(undefined)
  const [organisationFilter, setOrganisationFilter] = useState(defaultOrganisationFilter)
  const [appliedOrganisationFilter, setAppliedOrganisationFilter] = useState(defaultOrganisationFilter)
  const [organisationYear, setOrganisationYear] = useState(() => previousYear.toString())
  const [appliedOrganisationYear, setAppliedOrganisationYear] = useState(() => previousYear.toString())

  const triggerYearEndRolloverMutation = useTriggerYearEndRollover()
  const forfeitExpiredCarryoverMutation = useForfeitExpiredCarryover()

  const organisationsQuery = useGetAllOrganisations({
    query: {
      enabled: canFilterByOrganisation,
      staleTime: 30_000,
    },
  })
  const organisations = useMemo(() => organisationsQuery.data?.data ?? [], [organisationsQuery.data])

  const balanceParams = useMemo<UserBalancesParams>(() => ({
    year: previousYear,
  }), [previousYear])

  const organisationBalanceParams = useMemo<OrganisationBalancesParams | undefined>(() => {
    const parsedYear = Number(appliedOrganisationYear)
    if (Number.isNaN(parsedYear)) {
      return undefined
    }
    const params: OrganisationBalancesParams = { year: parsedYear }

    if (canFilterByOrganisation) {
      const parsedOrganisationId = Number(appliedOrganisationFilter)
      if (!Number.isNaN(parsedOrganisationId)) {
        params.organisationId = parsedOrganisationId
      }
      else if (typeof currentUser?.mainOrgainisationId === 'number') {
        params.organisationId = currentUser.mainOrgainisationId
      }
      else {
        return undefined
      }
      return params
    }

    if (typeof currentUser?.mainOrgainisationId === 'number') {
      params.organisationId = currentUser.mainOrgainisationId
      return params
    }

    return undefined
  }, [appliedOrganisationFilter, appliedOrganisationYear, canFilterByOrganisation, currentUser?.mainOrgainisationId])

  const canLoadOrganisationBalances = useMemo(() => {
    return Boolean(organisationBalanceParams)
  }, [organisationBalanceParams])
  const appliedOrganisationYearLabel = useMemo(() => {
    const parsed = Number(appliedOrganisationYear)
    if (Number.isNaN(parsed)) {
      return previousYear
    }
    return parsed
  }, [appliedOrganisationYear, previousYear])
  const hasValidOrganisationYearInput = useMemo(() => {
    const parsed = Number(organisationYear.trim())
    return organisationYear.trim() !== '' && !Number.isNaN(parsed)
  }, [organisationYear])

  const userBalancesQuery = useUserBalances(selectedUserId ?? 0, balanceParams, {
    query: {
      enabled: Boolean(selectedUserId),
    },
  })

  const organisationBalancesQuery = useOrganisationBalances(organisationBalanceParams, {
    query: {
      enabled: canLoadOrganisationBalances,
    },
  })

  const balances = useMemo(() => userBalancesQuery.data?.data ?? [], [userBalancesQuery.data?.data])
  const organisationBalances = useMemo(
    () => organisationBalancesQuery.data?.data ?? [],
    [organisationBalancesQuery.data?.data],
  )

  const organisationUserIds = useMemo(() => {
    const ids = new Set<number>()
    organisationBalances.forEach((row) => {
      if (typeof row.userId === 'number') {
        ids.add(row.userId)
      }
    })
    return Array.from(ids)
  }, [organisationBalances])

  const organisationUsersQuery = useQuery({
    queryKey: ['organisation-balance-users', organisationUserIds],
    queryFn: async () => {
      const response = await getAllUserDetails(organisationUserIds)
      return response.data
    },
    enabled: organisationUserIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const organisationUsersById = useMemo(() => {
    const map = new Map<number, UserDetailsResponse>()
    ;(organisationUsersQuery.data ?? []).forEach((user) => {
      if (typeof user.id === 'number') {
        map.set(user.id, user)
      }
    })
    return map
  }, [organisationUsersQuery.data])

  const organisationBalanceRows = useMemo<OrganisationBalanceRow[]>(() => {
    return organisationBalances.map((row) => {
      const mappedUser = typeof row.userId === 'number' ? organisationUsersById.get(row.userId) : undefined
      const userName = mappedUser?.name?.trim() || (typeof row.userId === 'number' ? `User ${row.userId}` : 'Unknown user')
      const userEmail = mappedUser?.email?.trim() || '—'

      return {
        ...row,
        userName,
        userEmail,
      }
    })
  }, [organisationBalances, organisationUsersById])

  const organisationUserSummaries = useMemo<OrganisationUserSummaryRow[]>(() => {
    const summaryByUser = new Map<number, OrganisationUserSummaryRow>()

    organisationBalanceRows.forEach((row) => {
      if (typeof row.userId !== 'number') {
        return
      }

      const existing = summaryByUser.get(row.userId)
      if (!existing) {
        summaryByUser.set(row.userId, {
          userId: row.userId,
          userName: row.userName,
          userEmail: row.userEmail,
          leaveTypes: 1,
          daysTakenToDate: row.daysTakenToDate ?? 0,
          carryoverRemaining: row.carryoverRemaining ?? 0,
          borrowedDays: row.borrowedDays ?? 0,
          totalRemaining: row.totalRemaining ?? 0,
          effectiveBalance: row.effectiveBalance ?? 0,
        })
        return
      }

      existing.leaveTypes += 1
      existing.daysTakenToDate += row.daysTakenToDate ?? 0
      existing.carryoverRemaining += row.carryoverRemaining ?? 0
      existing.borrowedDays += row.borrowedDays ?? 0
      existing.totalRemaining += row.totalRemaining ?? 0
      existing.effectiveBalance += row.effectiveBalance ?? 0
    })

    return Array.from(summaryByUser.values())
  }, [organisationBalanceRows])

  const leaveTypesWithAvailableBalance = useMemo(
    () => balances.filter(item => (item.totalRemaining ?? 0) > 0 || (item.carryoverRemaining ?? 0) > 0).length,
    [balances],
  )

  const usersWithCarryover = useMemo(
    () => organisationUserSummaries.filter(row => row.carryoverRemaining > 0).length,
    [organisationUserSummaries],
  )

  const usersWithNegativeBalance = useMemo(
    () => organisationUserSummaries.filter(row => row.effectiveBalance < 0).length,
    [organisationUserSummaries],
  )

  const selectedOrganisationSummary = useMemo(
    () => organisationUserSummaries.find(row => row.userId === selectedOrganisationDetailUserId),
    [organisationUserSummaries, selectedOrganisationDetailUserId],
  )

  const selectedOrganisationDetailRows = useMemo(
    () => organisationBalanceRows.filter(row => row.userId === selectedOrganisationDetailUserId),
    [organisationBalanceRows, selectedOrganisationDetailUserId],
  )

  const handleTriggerRollover = useCallback(async () => {
    const parsedFromYear = Number(rolloverFromYear)
    if (Number.isNaN(parsedFromYear)) {
      toast.error('Enter a valid year to roll over from.')
      return
    }

    try {
      const response = await triggerYearEndRolloverMutation.mutateAsync({
        params: { fromYear: parsedFromYear },
      })
      if (response?.data?.success === false) {
        throw new Error(response.data.message || 'Unable to trigger year-end rollover.')
      }
      toast.success(response?.data?.message || `Rollover triggered for ${parsedFromYear} → ${parsedFromYear + 1}.`)
      if (selectedUserId) {
        void userBalancesQuery.refetch()
      }
      if (canLoadOrganisationBalances) {
        void organisationBalancesQuery.refetch()
      }
    }
    catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to trigger year-end rollover.')
    }
  }, [
    canLoadOrganisationBalances,
    organisationBalancesQuery,
    rolloverFromYear,
    selectedUserId,
    triggerYearEndRolloverMutation,
    userBalancesQuery,
  ])

  const handleForfeitExpiredCarryover = useCallback(async () => {
    try {
      const response = await forfeitExpiredCarryoverMutation.mutateAsync()
      const message = (response as { data?: { message?: string } } | undefined)?.data?.message
      toast.success(message || 'Expired carryover forfeiture completed.')
      if (selectedUserId) {
        void userBalancesQuery.refetch()
      }
      if (canLoadOrganisationBalances) {
        void organisationBalancesQuery.refetch()
      }
    }
    catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to forfeit expired carryover.')
    }
  }, [
    canLoadOrganisationBalances,
    forfeitExpiredCarryoverMutation,
    organisationBalancesQuery,
    selectedUserId,
    userBalancesQuery,
  ])

  const handleLoadOrganisationBalances = useCallback(() => {
    const nextOrganisationFilter = organisationFilter.trim()
    const nextOrganisationYear = organisationYear.trim()
    if (canFilterByOrganisation && organisationFilter.trim() === '' && defaultOrganisationFilter === '') {
      toast.error('Select an organisation before loading balances.')
      return
    }
    if (!hasValidOrganisationYearInput) {
      toast.error('Enter a valid year before loading balances.')
      return
    }
    setOrganisationDetailsDialogOpen(false)
    setSelectedOrganisationDetailUserId(undefined)
    if (
      nextOrganisationFilter === appliedOrganisationFilter
      && nextOrganisationYear === appliedOrganisationYear
    ) {
      void organisationBalancesQuery.refetch()
      return
    }
    setAppliedOrganisationFilter(nextOrganisationFilter)
    setAppliedOrganisationYear(nextOrganisationYear)
  }, [
    appliedOrganisationFilter,
    appliedOrganisationYear,
    canFilterByOrganisation,
    defaultOrganisationFilter,
    hasValidOrganisationYearInput,
    organisationYear,
    setOrganisationDetailsDialogOpen,
    organisationBalancesQuery,
    organisationFilter,
  ])

  const handleResetOrganisationFilters = useCallback(() => {
    const resetYear = previousYear.toString()
    setOrganisationFilter(defaultOrganisationFilter)
    setOrganisationYear(resetYear)
    setOrganisationDetailsDialogOpen(false)
    setSelectedOrganisationDetailUserId(undefined)
    if (defaultOrganisationFilter === appliedOrganisationFilter && resetYear === appliedOrganisationYear) {
      void organisationBalancesQuery.refetch()
      return
    }
    setAppliedOrganisationFilter(defaultOrganisationFilter)
    setAppliedOrganisationYear(resetYear)
  }, [appliedOrganisationFilter, appliedOrganisationYear, defaultOrganisationFilter, organisationBalancesQuery, previousYear])

  const organisationOptions = useMemo(() => (
    organisations
      .filter(org => typeof org.id === 'number')
      .map(org => ({
        id: org.id,
        value: String(org.id),
        name: org.name ?? `Organisation ${org.id}`,
      }))
  ), [organisations])

  const organisationSummaryColumns = useMemo<ColumnConfig<OrganisationUserSummaryRow>[]>(() => ([
    {
      key: 'userName',
      header: 'User',
    },
    {
      key: 'userEmail',
      header: 'Email',
    },
    {
      key: 'leaveTypes',
      header: 'Leave Types',
      type: 'number',
      cell: value => new Intl.NumberFormat().format(Number(value) || 0),
    },
    {
      key: 'daysTakenToDate',
      header: 'Taken To Date',
      cell: value => formatDays(Number(value)),
    },
    {
      key: 'carryoverRemaining',
      header: 'Carryover Remaining',
      cell: value => formatDays(Number(value)),
    },
    {
      key: 'borrowedDays',
      header: 'Borrowed Days',
      cell: value => formatDays(Number(value)),
    },
    {
      key: 'totalRemaining',
      header: 'Total Remaining',
      cell: value => formatDays(Number(value)),
    },
    {
      key: 'effectiveBalance',
      header: 'Effective Balance',
      cell: value => formatDays(Number(value)),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedOrganisationDetailUserId(row.userId)
              setOrganisationDetailsDialogOpen(true)
            }}
          >
            View Details
          </Button>
        </div>
      ),
    },
  ]), [])

  const organisationDetailColumns = useMemo<ColumnConfig<OrganisationBalanceRow>[]>(() => ([
    {
      key: 'userName',
      header: 'User',
    },
    {
      key: 'userEmail',
      header: 'Email',
    },
    {
      key: 'leaveTypeName',
      header: 'Leave Type',
      cell: (_value, row) => row.leaveTypeName ?? row.leaveTypeCode ?? '—',
    },
    {
      key: 'annualEntitlement',
      header: 'Annual Entitlement',
      cell: (_value, row) => formatDays(row.annualEntitlement),
    },
    {
      key: 'daysTakenToDate',
      header: 'Taken To Date',
      cell: (_value, row) => formatDays(row.daysTakenToDate),
    },
    {
      key: 'annualRemaining',
      header: 'Annual Remaining',
      cell: (_value, row) => formatDays(row.annualRemaining),
    },
    {
      key: 'carryoverRemaining',
      header: 'Carryover Remaining',
      cell: (_value, row) => formatDays(row.carryoverRemaining),
    },
    {
      key: 'carryoverExpiryDate',
      header: 'Carryover Expiry',
      cell: (_value, row) => (row.carryoverExpiryDate ? formatDate(new Date(row.carryoverExpiryDate)) : '—'),
    },
    {
      key: 'borrowedDays',
      header: 'Borrowed Days',
      cell: (_value, row) => formatDays(row.borrowedDays),
    },
    {
      key: 'totalRemaining',
      header: 'Total Remaining',
      cell: (_value, row) => formatDays(row.totalRemaining),
    },
    {
      key: 'effectiveBalance',
      header: 'Effective Balance',
      cell: (_value, row) => formatDays(row.effectiveBalance),
    },
  ]), [])

  const selectedUserBalanceColumns = useMemo<ColumnConfig<LeaveBalanceDto>[]>(() => ([
    {
      key: 'leaveTypeName',
      header: 'Leave Type',
      cell: (_value, row) => row.leaveTypeName ?? row.leaveTypeCode ?? '—',
    },
    {
      key: 'annualEntitlement',
      header: 'Annual Entitlement',
      cell: (_value, row) => formatDays(row.annualEntitlement),
    },
    {
      key: 'daysTakenToDate',
      header: 'Taken To Date',
      cell: (_value, row) => formatDays(row.daysTakenToDate),
    },
    {
      key: 'annualRemaining',
      header: 'Annual Remaining',
      cell: (_value, row) => formatDays(row.annualRemaining),
    },
    {
      key: 'carryoverRemaining',
      header: 'Carryover Remaining',
      cell: (_value, row) => formatDays(row.carryoverRemaining),
    },
    {
      key: 'carryoverExpiryDate',
      header: 'Carryover Expiry',
      cell: (_value, row) => (row.carryoverExpiryDate ? formatDate(new Date(row.carryoverExpiryDate)) : '—'),
    },
    {
      key: 'borrowedDays',
      header: 'Borrowed Days',
      cell: (_value, row) => formatDays(row.borrowedDays),
    },
    {
      key: 'borrowableRemaining',
      header: 'Borrowable Remaining',
      cell: (_value, row) => formatDays(row.borrowableRemaining),
    },
    {
      key: 'totalRemaining',
      header: 'Total Remaining',
      cell: (_value, row) => formatDays(row.totalRemaining),
    },
    {
      key: 'effectiveBalance',
      header: 'Effective Balance',
      cell: (_value, row) => formatDays(row.effectiveBalance),
    },
  ]), [])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Leave Balances</h1>
          <p className="text-sm text-muted-foreground">
            Review organisation and individual leave balances, including carryover and borrowing context.
          </p>
        </div>
        <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="default">
              Leave Balance Maintenance
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Leave Balance Maintenance</DialogTitle>
              <DialogDescription>
                Trigger rollover and carryover maintenance actions for historical years.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-2">
                <Label htmlFor="rollover-from-year">Rollover From Year</Label>
                <Input
                  id="rollover-from-year"
                  type="number"
                  min={2000}
                  value={rolloverFromYear}
                  onChange={event => setRolloverFromYear(event.target.value)}
                  placeholder="2025"
                  className="w-44"
                />
              </div>
              <Button
                type="button"
                onClick={() => void handleTriggerRollover()}
                disabled={triggerYearEndRolloverMutation.isPending}
              >
                {triggerYearEndRolloverMutation.isPending ? 'Triggering...' : 'Trigger Year-End Rollover'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleForfeitExpiredCarryover()}
                disabled={forfeitExpiredCarryoverMutation.isPending}
              >
                {forfeitExpiredCarryoverMutation.isPending ? 'Processing...' : 'Forfeit Expired Carryover'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={value => setActiveTab(value as 'organisation' | 'user')} className="space-y-4">
        <TabsList>
          <TabsTrigger value="organisation">Organisation Balances</TabsTrigger>
          <TabsTrigger value="user">User Balances</TabsTrigger>
        </TabsList>

        <TabsContent value="organisation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Organisation Leave Balances</CardTitle>
              <CardDescription>
                View leave balances across the organisation by user and leave type for
                {' '}
                {appliedOrganisationYearLabel}
                .
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid items-end gap-3 md:grid-cols-[minmax(0,2fr)_10rem_auto_auto]">
                {canFilterByOrganisation
                  ? (
                    <OrganisationSearchInput
                      id="organisation-balance-filter"
                      label="Organisation"
                      placeholder="Select organisation"
                      value={organisationFilter}
                      onValueChange={setOrganisationFilter}
                      disabled={organisationsQuery.isPending || organisationsQuery.isError}
                      options={organisationOptions}
                      isLoading={organisationsQuery.isPending}
                    />
                  )
                  : null}
                <div className="space-y-2">
                  <Label htmlFor="organisation-balance-year">Year</Label>
                  <Input
                    id="organisation-balance-year"
                    type="number"
                    min={2000}
                    value={organisationYear}
                    onChange={event => setOrganisationYear(event.target.value)}
                    placeholder={previousYear.toString()}
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleLoadOrganisationBalances}
                  disabled={
                    (canFilterByOrganisation && organisationFilter.trim() === '' && defaultOrganisationFilter === '')
                    || !hasValidOrganisationYearInput
                  }
                >
                  Load Data
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResetOrganisationFilters}
                >
                  Reset Filters
                </Button>
              </div>

              {canFilterByOrganisation && organisationsQuery.isError
                ? (
                  <Alert variant="destructive">
                    <AlertTitle>Unable to load organisations</AlertTitle>
                    <AlertDescription>{organisationsQuery.error?.message ?? 'Please refresh and try again.'}</AlertDescription>
                  </Alert>
                )
                : null}

              {canFilterByOrganisation && !canLoadOrganisationBalances
                ? (
                  <Alert>
                    <AlertTitle>{organisationFilter.trim() === '' ? 'Select an organisation' : 'Load data'}</AlertTitle>
                    <AlertDescription>
                      {organisationFilter.trim() === ''
                        ? 'Choose an organisation to view organisation-wide balances.'
                        : 'Click Load Data to apply your filter.'}
                    </AlertDescription>
                  </Alert>
                )
                : null}

              {!canFilterByOrganisation && !canLoadOrganisationBalances
                ? (
                  <Alert variant="destructive">
                    <AlertTitle>Organisation not available</AlertTitle>
                    <AlertDescription>Your account does not have a mapped organisation.</AlertDescription>
                  </Alert>
                )
                : null}

              {canLoadOrganisationBalances && organisationBalancesQuery.isError
                ? (
                  <Alert variant="destructive">
                    <AlertTitle>Unable to load organisation balances</AlertTitle>
                    <AlertDescription>{organisationBalancesQuery.error?.message ?? 'Please try again.'}</AlertDescription>
                  </Alert>
                )
                : null}

              {organisationUsersQuery.isError
                ? (
                  <Alert>
                    <AlertTitle>User profiles partially unavailable</AlertTitle>
                    <AlertDescription>
                      Showing user IDs where names could not be resolved.
                    </AlertDescription>
                  </Alert>
                )
                : null}

              {canLoadOrganisationBalances
                ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Users in Snapshot</CardDescription>
                        <CardTitle className="text-2xl">{organisationUserSummaries.length}</CardTitle>
                      </CardHeader>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Balance Rows</CardDescription>
                        <CardTitle className="text-2xl">{organisationBalanceRows.length}</CardTitle>
                      </CardHeader>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Users With Carryover</CardDescription>
                        <CardTitle className="text-2xl">{usersWithCarryover}</CardTitle>
                      </CardHeader>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Users Negative Balance</CardDescription>
                        <CardTitle className="text-2xl">{usersWithNegativeBalance}</CardTitle>
                      </CardHeader>
                    </Card>
                  </div>
                )
                : null}

              <div className="space-y-2">
                <p className="text-sm font-medium">Balance Snapshot By User</p>
                <EnhancedDataTable
                  data={organisationUserSummaries}
                  columns={organisationSummaryColumns}
                  loading={Boolean(canLoadOrganisationBalances && (organisationBalancesQuery.isPending || organisationUsersQuery.isPending))}
                  emptyStateMessage={canLoadOrganisationBalances
                    ? `No organisation balances found for ${appliedOrganisationYearLabel}.`
                    : 'Select an organisation to load balances.'}
                  enableColumnFilters={false}
                  enableExport={false}
                />
              </div>

              <Dialog open={organisationDetailsDialogOpen} onOpenChange={setOrganisationDetailsDialogOpen}>
                <DialogContent className="w-[98vw] max-w-[98vw] sm:max-w-[95vw] xl:max-w-[90vw] 2xl:max-w-[1800px] max-h-[92vh] overflow-hidden">
                  <DialogHeader>
                    <DialogTitle>Detailed Balances By Leave Type</DialogTitle>
                    <DialogDescription>
                      Showing details for
                      {' '}
                      <span className="font-medium">{selectedOrganisationSummary?.userName ?? 'selected user'}</span>
                      {selectedOrganisationSummary?.userEmail ? ` (${selectedOrganisationSummary.userEmail})` : ''}
                      {' '}
                      in
                      {' '}
                      {appliedOrganisationYearLabel}
                      .
                    </DialogDescription>
                  </DialogHeader>
                  {selectedOrganisationDetailUserId
                    ? (
                      <div className="max-h-[70vh] overflow-auto">
                        <EnhancedDataTable
                          data={selectedOrganisationDetailRows}
                          columns={organisationDetailColumns}
                          loading={Boolean(canLoadOrganisationBalances && (organisationBalancesQuery.isPending || organisationUsersQuery.isPending))}
                          emptyStateMessage={canLoadOrganisationBalances
                            ? `No detailed balance records found for this user in ${appliedOrganisationYearLabel}.`
                            : 'Select an organisation to load balances.'}
                          enableColumnFilters={false}
                          enableExport={false}
                          className="min-w-[1100px]"
                        />
                      </div>
                    )
                    : (
                      <Alert>
                        <AlertTitle>Select a user from snapshot</AlertTitle>
                        <AlertDescription>Click View Details in the snapshot table to load user balances.</AlertDescription>
                      </Alert>
                    )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="user" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Leave Balances</CardTitle>
              <CardDescription>
                Select a user to see balance details for
                {' '}
                {previousYear}
                .
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <UserSearchInput
                  id="balance-user-search"
                  label="User"
                  placeholder="Search user by name/email"
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                />
              </div>

              {!selectedUserId
                ? (
                  <Alert>
                    <AlertTitle>Select a user</AlertTitle>
                    <AlertDescription>
                      Choose a user first to load leave balance information.
                    </AlertDescription>
                  </Alert>
                )
                : null}

              {selectedUserId && userBalancesQuery.isError
                ? (
                  <Alert variant="destructive">
                    <AlertTitle>Unable to load user balances</AlertTitle>
                    <AlertDescription>{userBalancesQuery.error?.message ?? 'Please try again.'}</AlertDescription>
                  </Alert>
                )
                : null}

              {selectedUserId
                ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>Leave Types</CardDescription>
                        <CardTitle className="text-2xl">{balances.length}</CardTitle>
                      </CardHeader>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardDescription>With Available Balance</CardDescription>
                        <CardTitle className="text-2xl">{leaveTypesWithAvailableBalance}</CardTitle>
                      </CardHeader>
                    </Card>
                  </div>
                )
                : null}

              <EnhancedDataTable
                data={balances}
                columns={selectedUserBalanceColumns}
                loading={Boolean(selectedUserId && userBalancesQuery.isPending)}
                emptyStateMessage={selectedUserId ? `No balances found for this user in ${previousYear}.` : 'Select a user to view balances.'}
                enableColumnFilters={false}
                enableExport={false}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
